import { resolveTimezone } from '@web-app/utils/date-time.utils';
import { parseHealthieDate } from '@web-app/utils/nutrition/dates';
import { compareAsc, compareDesc, isValid, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { JournalEntry } from './types';

/**
 * Groups journal entries by date (YYYY-MM-DD) in the patient's timezone.
 * This ensures entries are grouped by the day they were posted, not the day
 * the viewer is experiencing.
 */
export const groupEntriesByDate = (
  entries: JournalEntry[],
): Map<string, JournalEntry[]> => {
  const grouped = new Map<string, JournalEntry[]>();

  entries.forEach((entry) => {
    const createdAt = parseHealthieDate(entry.createdAt);
    if (!isValid(createdAt)) return;

    const timezone = resolveTimezone(entry.timezone);
    const date = formatInTimeZone(createdAt, timezone, 'yyyy-MM-dd');

    const existing = grouped.get(date) || [];
    grouped.set(date, [...existing, entry]);
  });

  // Sort entries within each day chronologically (oldest first)
  grouped.forEach((dayEntries, date) => {
    grouped.set(
      date,
      dayEntries.sort((a, b) => {
        const dateA = parseHealthieDate(a.createdAt);
        const dateB = parseHealthieDate(b.createdAt);
        if (!isValid(dateA) || !isValid(dateB)) return 0;
        return compareAsc(dateA, dateB);
      }),
    );
  });

  return grouped;
};

/**
 * Formats a date string for display in timeline headers.
 * Returns "Today", "Yesterday", or formatted date based on patient's timezone.
 *
 * @param dateString - Date in YYYY-MM-DD format
 * @param timezone - IANA timezone identifier (e.g., "America/New_York")
 */
export const formatDateHeader = (
  dateString: string,
  timezone: string,
): string => {
  // Get today's date in the specified timezone
  const todayInTz = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');

  if (dateString === todayInTz) {
    return 'Today';
  }

  const yesterdayDate = subDays(new Date(), 1);
  const yesterdayInTz = formatInTimeZone(yesterdayDate, timezone, 'yyyy-MM-dd');

  if (dateString === yesterdayInTz) {
    return 'Yesterday';
  }

  // Format the date in the specified timezone
  return formatInTimeZone(new Date(dateString), timezone, 'EEEE, MMM d');
};

/**
 * Sorts date strings in reverse chronological order (newest first).
 */
export const sortDatesDescending = (dates: string[]): string[] => {
  return [...dates].sort((a, b) => compareDesc(new Date(a), new Date(b)));
};

export { resolveTimezone } from '@web-app/utils/date-time.utils';
